import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';

/** Body of `GET /api/realtime/token` (kredit-api-MT, socket v2 R2). */
export interface RealtimeTokenResponse {
  /** Base URL of the realtime server the console must connect to. */
  url: string | null;
  /** Channel token (HS256, `scope: console`, 60 min), sent in `auth.token` of the handshake. */
  token: string | null;
  expiresInSeconds: number;
}

/** Storage key of the console JWT, the same one `AuthService` and `AuthInterceptorService` use. */
const CONSOLE_JWT_STORAGE_KEY = 'x-token';

/**
 * Mints the console channel token from kredit.
 *
 * **It deliberately bypasses the HTTP interceptors** (`HttpBackend`, not the
 * app's `HttpClient`). `AuthService` restores the session inside its own
 * constructor and opens the socket from there; going through `HttpClient`
 * made that first request instantiate `AuthInterceptorService`, which
 * injects `AuthService` while it is still being constructed. In an optimized
 * build Angular does not throw on that cycle: it hands the interceptor an
 * empty placeholder, the root interceptor keeps it, and every response of the
 * app fails with `getIsLoggingOut is not a function`.
 *
 * So the console JWT is read here, from the same storage key the interceptor
 * uses, and sent in `authorization` exactly as the interceptor sends it (raw,
 * no `Bearer`). This call does not take part in the sliding-token renewal;
 * every other call of the app still does.
 *
 * Failures surface as `HttpErrorResponse`: 404 = kredit without the endpoint
 * yet (rollout), 503 = kredit without the channel secret, 401/403 = no session
 * or a role not allowed on the channel.
 */
@Injectable({
  providedIn: 'root'
})
export class RealtimeTokenService {

  private readonly endpoint = `${environment.BASE_LA}/api/realtime/token`;
  /** An `HttpClient` wired straight to the backend: no interceptor chain, no DI cycle. */
  private readonly http: HttpClient;

  constructor(backend: HttpBackend) {
    this.http = new HttpClient(backend);
  }

  mint(): Promise<RealtimeTokenResponse> {
    const consoleJwt = localStorage.getItem(CONSOLE_JWT_STORAGE_KEY);
    if (!consoleJwt) {
      return Promise.reject(
        new HttpErrorResponse({ status: 401, statusText: 'No console session', url: this.endpoint }),
      );
    }
    const headers = new HttpHeaders({ authorization: consoleJwt });
    return lastValueFrom(this.http.get<RealtimeTokenResponse>(this.endpoint, { headers }));
  }
}
